<template>
  <div id="raise-table">
    <div class="raising-container">
      <!-- Card Grid View -->
      <div class="raising-grid" v-if="displayedTokens.length > 0">
        <div 
          v-for="token in displayedTokens" 
          :key="token._id"
          class="raising-card"
          @click="gotoTokenPage(token)"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="company-logo">
              <img 
                :src="token.image" 
                :alt="token.name"
                loading="lazy"
                @error="handleImageError"
              />
            </div>
            <div class="company-info">
              <h3 class="company-name">{{ capitalizeEachWord(token.name) }}</h3>
              <span class="company-symbol">{{ token.symbol }}</span>
              <ShariaBadge :token="token" size="sm" class="company-sharia" />
            </div>
            <DealTypeBadge :badge="viewOf(token).badge" />
          </div>

          <!-- What this deal is about in one figure: a note's yield, a metal's or a stock's price,
               a fund's rate. -->
          <div v-if="viewOf(token).hero" class="card-hero">
            <span class="hero-value">{{ viewOf(token).hero.value }}</span>
            <span class="hero-unit">{{ viewOf(token).hero.unit }}</span>
            <span v-if="viewOf(token).change" class="hero-change" :class="viewOf(token).change.tone">{{ viewOf(token).change.text }}</span>
          </div>

          <!-- Offer state. Only for offerings with a period — a continuously filling deal has
               nothing to say here that the progress bar below does not already show. -->
          <div v-if="hasOfferPeriod(token)" class="card-offer-state">
            <OfferingStatusBadge :token="token" />
          </div>

          <!-- Progress Section
               A book build mints no units until it prices, so "% funded" sits at 0 however
               strong demand is. Where there is a book, the meaningful number is how far the
               offer is subscribed. -->
          <!-- Not for metals and NGX stocks: they sell at the market price with no goal to reach,
               so the card says what is available instead (below). -->
          <div v-if="!byAvailability(token)" class="progress-section">
            <div class="progress-bar-container">
              <div
                class="progress-bar"
                :class="{ 'progress-bar--over': isOversubscribed(token) }"
                :style="{ width: barWidth(token) + '%' }"
              ></div>
            </div>
            <div class="progress-stats">
              <template v-if="hasOfferPeriod(token)">
                <span class="progress-percent">
                  <template v-if="bookFor(token)">{{ subscribedPercent(token) }}% subscribed</template>
                  <template v-else>Open for bids</template>
                </span>
                <span
                  v-if="isOversubscribed(token)"
                  class="progress-tag"
                >Oversubscribed</span>
                <span v-else-if="bookFor(token)" class="progress-amount">
                  {{ formatAmountToDollar(bookFor(token).summary.total_demand, 0, currency(token)) }} bid
                </span>
              </template>
              <template v-else>
                <span class="progress-percent">{{ getProgress(token) }}% funded</span>
                <span class="progress-amount">{{ formatAmountToDollar((token.total_raised || 0) * (token.price?.exchange || 0), 0, currency(token)) }} raised</span>
              </template>
            </div>
          </div>

          <!-- Card Details -->
          <div class="card-details">
            <div class="detail-item" v-if="byAvailability(token)">
              <span class="detail-label">Available</span>
              <span class="detail-value">{{ availableOf(token) }}</span>
            </div>
            <div class="detail-item" v-else>
              <span class="detail-label">Target</span>
              <span class="detail-value">{{ formatAmountToDollar(token.raise_amount, 0, currency(token)) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Min. Investment</span>
              <span class="detail-value">{{ formatAmountToDollar(token.min_trade?.buy || 0, 0, currency(token)) }}</span>
            </div>
            <!-- The badge above carries urgency ("closes in 6 hours"); this is the moment
                 someone plans around, in market time. -->
            <div class="detail-item detail-item--wide" v-if="hasOfferPeriod(token)">
              <span class="detail-label">Closes</span>
              <span class="detail-value">
                {{ formatOfferMoment(token.offer_closes_at, token.market_timezone) }}
              </span>
            </div>
          </div>

          <!-- Card Action -->
          <div class="card-action">
            <span class="invest-btn" :class="{ 'invest-btn--muted': !callToAction(token).open }">
              {{ ctaLabel(token) }}
              <svg v-if="callToAction(token).open" class="tw-w-4 tw-h-4" viewBox="0 0 16 16" fill="none">
                <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div class="empty-container" v-else-if="!smallLoading">
        <EmptyState>
          <template v-slot:message>
            <p class="empty-message" v-if="hasSearchQuery">
              No investment with that name exists, please try a different name or symbol
            </p>
            <p class="empty-message" v-else-if="filtering">
              No deals of that kind are raising right now
            </p>
            <p class="empty-message" v-else>
              There are currently no investments raising, please check back later
            </p>
          </template>
        </EmptyState>
      </div>

      <!-- Loading State -->
      <SmallLoader v-if="smallLoading && table === 'raisingTable'" />

      <!-- Pagination -->
      <div v-if="!limit && !filtering && companiesRaising?.length > 0" class="pagination-container">
        <Pagination
          :totalPage="raisingPages"
          :currentPage="raisingPage"
          :maxVisible="5"
          @pageChange="gotoPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/navigation/Pagination";
import EmptyState from "@/components/notification/EmptyState.vue";
import { capitalizeEachWord, formatAmountToDollar } from "@/utils/helpers.js";
import { formatOfferMoment, hasOfferPeriod, resolveTokenState } from "@/utils/offering";
import OfferingStatusBadge from "@/components/general/OfferingStatusBadge.vue";
import DealTypeBadge from "@/components/general/DealTypeBadge.vue";
import ShariaBadge from "@/components/general/ShariaBadge.vue";
import { availableLabel, listingView, sellsByAvailability } from "@/utils/trade-view";
import { mapActions, mapGetters, mapState } from "vuex";

  /** How many deals a filter loads to narrow: all of them, at today's volumes. */
  const FILTER_LIMIT = 100;

export default {
    name: "RaisingTable",

    components: { Pagination, EmptyState, OfferingStatusBadge, DealTypeBadge, ShariaBadge },

    data() {
      return {
        table: "",
        // Book summaries keyed by token id, for offerings that have one.
        books: {},
      };
    },

    computed: {
      ...mapState({
        smallLoading: (state) => state.smallLoading,
      }),
      ...mapGetters("TokenModule", [
        "companiesRaising",
        "raisingPage",
        "raisingPages",
      ]),
      // Filtered client-side against the page already fetched. The endpoint pages server-side,
      // so this narrows the current page rather than the whole set — acceptable while volumes
      // are small, but it wants a query param once there are enough offerings to page through.
      filteredTokens() {
        let list = this.companiesRaising || [];
        if (this.offeringFilter) {
          list = list.filter((token) => (token.offering_type || "FIXED_PRICE") === this.offeringFilter);
        }
        if (this.typeFilter) list = list.filter((token) => this.viewOf(token).badge.tone === this.typeFilter);
        return list;
      },
      // The filters run on what is loaded, so while one is on the whole list is loaded and the
      // page numbers, which count the unfiltered list, are hidden.
      filtering() {
        return Boolean(this.offeringFilter || this.typeFilter);
      },
      // One view per card, worked out once per list rather than on every render.
      views() {
        return new Map((this.companiesRaising || []).map((token) => [token._id, listingView(token)]));
      },
      displayedTokens() {
        if (this.limit > 0) {
          return this.filteredTokens.slice(0, this.limit);
        }
        return this.filteredTokens;
      },
    },

    props: {
      // Preview cap for dashboard placements. 0 renders the full paginated list.
      limit: {
        type: Number,
        required: false,
        default: 0,
      },
      // Empty means no filter. Deals predating offering types have no value stored and are
      // fixed price by definition, so they must match that option rather than disappear.
      offeringFilter: {
        type: String,
        required: false,
        default: "",
      },
      // A badge tone from listingView (debt, equity, safe, fund, commodity, ngx); empty for all.
      typeFilter: {
        type: String,
        required: false,
        default: "",
      },
      hasSearchQuery: {
        type: Boolean,
        required: false,
        default: () => {
          return false;
        },
      },
    },

    mounted() {
      if (!this.hasSearchQuery) {
        if (this.filtering) this.loadAll();
        else this.getDetails();
      }
    },

    watch: {
      filtering(on) {
        if (this.hasSearchQuery) return;
        if (on) this.loadAll();
        else this.getDetails();
      },
      // Fires on first load, pagination and search alike, so every visible card gets its
      // demand figures without each caller having to remember to ask.
      displayedTokens: {
        immediate: true,
        handler(tokens) {
          this.loadBooks(tokens);
        },
      },
    },

    methods: {
      hasOfferPeriod,
      formatOfferMoment,
      /**
       * "Invest Now" is only true of a deal that fills on the spot. A book build that has not
       * opened, or has closed, would be promising something it cannot do — and the card is
       * clickable, so the label is the only thing setting the expectation.
       */
      byAvailability(token) {
        return sellsByAvailability(token);
      },
      availableOf(token) {
        return availableLabel(token);
      },
      viewOf(token) {
        return this.views.get(token._id) || listingView(token);
      },
      // A deal that fills on the spot says what you are buying: "Buy Silver", "Buy Shares".
      ctaLabel(token) {
        const action = this.callToAction(token);
        return action.open && action.label === "Invest Now" ? this.viewOf(token).cta : action.label;
      },
      callToAction(token) {
        const state = resolveTokenState(token);
        if (!state.windowed) return { label: "Invest Now", open: true };
        if (state.takingBids) return { label: "Place a bid", open: true };
        if (state.detail) return { label: state.detail, open: false };
        return { label: state.label, open: false };
      },
      ...mapActions("TokenModule", ["getCompaniesRaising", "fetchOfferingBook"]),

      capitalizeEachWord,
      formatAmountToDollar,

      gotoTokenPage(token) {
        this.$router.push({
          name: "TokenOverview",
          params: { symbol: token.symbol, id: token._id },
        });
      },

      /**
       * Demand for each windowed offering, keyed by token id.
       *
       * The token document carries no demand figures — they only exist on the book endpoint —
       * so this is one aggregate request per card that actually has a book. Legacy
       * continuously-filling deals are skipped entirely.
       */
      async loadBooks(tokens) {
        const windowed = (tokens || []).filter((t) => hasOfferPeriod(t) && t._id);
        if (!windowed.length) return;
        const results = await Promise.all(
          windowed.map((t) => this.fetchOfferingBook({ id: t._id }))
        );
        const next = { ...this.books };
        windowed.forEach((t, i) => {
          if (results[i]?.summary) next[t._id] = results[i];
        });
        this.books = next;
      },

      bookFor(token) {
        return this.books[token?._id] || null;
      },

      subscribedPercent(token) {
        const cover = Number(this.bookFor(token)?.summary?.cover_ratio || 0);
        return (cover * 100).toFixed(1);
      },

      isOversubscribed(token) {
        return this.bookFor(token)?.summary?.subscription_status === 'OVERSUBSCRIBED';
      },

      // Cover can exceed 1, so the bar is capped; the tag carries the "beyond full" signal.
      barWidth(token) {
        if (!hasOfferPeriod(token)) return this.getProgress(token);
        const book = this.bookFor(token);
        if (!book) return 0;
        return Math.min(Number(book.summary.cover_ratio || 0) * 100, 100).toFixed(1);
      },

      getProgress(token) {
        const currentValue = (token.total_raised || 0) * (token.price?.exchange || 0);
        const targetAmount = token.raise_amount || 1;
        return Math.min(((currentValue / targetAmount) * 100), 100).toFixed(1);
      },

      currency(token) {
        return token.currency || 'USD';
      },

      handleImageError(e) {
        e.target.src = require("@/assets/img/token-icon.svg");
      },

      async getDetails() {
        let limit;
        this.table = "raisingTable";
        // Fetch only what will be shown. Driven by the same prop as the render cap, so a
        // preview does not pull ten rows to display two.
        this.limit > 0 ? (limit = this.limit) : (limit = 10);
        const query = { page: 1, limit: limit };
        await this.getCompaniesRaising(query);
        this.table = "";
      },

      // Everything raising, for a filter to narrow. Raising deals number well under this.
      async loadAll() {
        this.table = "raisingTable";
        await this.getCompaniesRaising({ page: 1, limit: FILTER_LIMIT });
        this.table = "";
      },

      async gotoPage(page) {
        this.table = "raisingTable";
        const payload = {
          page: page,
          limit: 10,
        };
        await this.getCompaniesRaising(payload);
        this.table = "";
      },
    },
  };
</script>

<style lang="scss" scoped>
#raise-table {
  @include fadeIn;

  .raising-container {
    width: 100%;
  }

  .raising-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
  }

  .raising-card {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 11px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #4D84FF;
      box-shadow: 0 4px 20px rgba(77, 132, 255, 0.12);
      transform: translateY(-2px);

      .invest-btn {
        color: #4D84FF;

        // Card hover turns the action brand-blue, which reads as "you can do this now".
        // A closed or unopened offer must not pick that up.
        &--muted {
          color: #64748B;
        }
      }
    }
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .company-logo {
    width: 48px;
    height: 48px;
    border-radius: 4px;
    overflow: hidden;
    background: #F8FAFC;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .company-info {
    flex: 1;
    min-width: 0;
  }

  .company-name {
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 2px 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .company-symbol {
    font-size: 13px;
    color: #64748B;
    font-weight: 400;
  }

  .company-sharia {
    margin-left: 8px;
    vertical-align: 1px;
  }

  .card-hero {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    margin-bottom: 14px;
  }

  .hero-value {
    font-size: 22px;
    font-weight: 500;
    color: #0F172A;
  }

  .hero-unit {
    font-size: 13px;
    color: #64748B;
  }

  .hero-change {
    font-size: 12px;
    font-weight: 500;
    color: #64748B;

    &.up { color: #059669; }
    &.down { color: #DC2626; }
  }

  .progress-section {
    margin-bottom: 16px;
  }

  .progress-bar-container {
    height: 6px;
    background: #E2E8F0;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  .progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #4D84FF, #6B9AFF);
    border-radius: 3px;
    transition: width 0.5s ease;
  }

  .progress-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .progress-percent {
    font-size: 13px;
    font-weight: 500;
    color: #4D84FF;
  }

  .progress-amount {
    font-size: 12px;
    color: #64748B;
  }

  .card-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid #F1F5F9;
    border-bottom: 1px solid #F1F5F9;
    margin-bottom: 12px;
  }

  // The close moment carries a date, a time and a zone, so it gets its own row rather than
  // being squeezed into a third column and wrapping mid-timestamp.
  .detail-item--wide {
    grid-column: 1 / -1;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .detail-label {
    font-size: 11px;
    color: #94A3B8;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .detail-value {
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }

  .card-action {
    display: flex;
    justify-content: flex-end;
  }

  .invest-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    transition: color 0.2s ease;

    // A closed or not-yet-open offer still opens its page — there is plenty to read there —
    // but the action should not look like it is inviting money right now.
    &--muted {
      font-weight: 400;
    }
  }

  .card-offer-state {
    margin-bottom: 12px;
  }

  .empty-container {
    padding: 40px 20px;
    text-align: center;
  }

  .empty-message {
    color: #64748B;
    font-size: 14px;
    margin: 12px 0;
  }

  .pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    padding-right: 8px;
  }
}

  .progress-tag {
    font-size: 11px;
    font-weight: 600;
    color: #047857;
    background: #ECFDF5;
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
  }

  /* Full cover reads differently from a part-filled bar, so the fill changes colour rather
     than just stopping at 100%. */
  .progress-bar--over {
    background: #10B981;
  }
</style>
